package com.evoapps.evoshift.viewmodels

import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.unit.IntRect
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.user.RemoveProfilePhotoUseCase
import com.evoapps.evoshift.domain.usecase.user.UploadProfilePhotoUseCase
import com.evoapps.evoshift.photo.AvatarImages
import com.evoapps.evoshift.photo.PhotoProblemException
import com.evoapps.evoshift.photo.PreparedPhoto
import com.evoapps.evoshift.photo.ProfilePhotoProcessor
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.profile_photo_not_an_image
import evoshift.composeapp.generated.resources.profile_photo_read_failed
import evoshift.composeapp.generated.resources.profile_photo_removed
import evoshift.composeapp.generated.resources.profile_photo_too_large
import evoshift.composeapp.generated.resources.profile_photo_unreadable
import evoshift.composeapp.generated.resources.profile_photo_updated
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class ProfilePhotoUiState(
    val uid: String? = null,
    val displayName: String = "",
    /** What the avatar shows: the saved photo, or optimistically the new one / none. */
    val photoUrl: String? = null,
    /** The photo just cropped, shown while it uploads (optimistic). */
    val preview: ImageBitmap? = null,
    /** A picked photo being decoded (the avatar shows progress). */
    val isPreparing: Boolean = false,
    /** The picked photo, upright: the crop screen is open. */
    val cropSource: PreparedPhoto? = null,
    /** The upload's progress 0..1 while uploading, null otherwise. */
    val uploadProgress: Float? = null,
    val isRemoving: Boolean = false,
    val message: UiText? = null,
    val error: UiText? = null
) {
    val isBusy: Boolean get() = isPreparing || uploadProgress != null || isRemoving
    val hasPhoto: Boolean get() = preview != null || !photoUrl.isNullOrBlank()
}

sealed interface ProfilePhotoEvent {
    /** A picker returned a file (null: it could not be read). */
    data class Picked(val bytes: ByteArray?) : ProfilePhotoEvent
    /** Save on the crop screen: [crop] in the picked photo's pixels. */
    data class CropConfirmed(val crop: IntRect) : ProfilePhotoEvent
    data object CropCancelled : ProfilePhotoEvent
    data object Remove : ProfilePhotoEvent
    data object MessageShown : ProfilePhotoEvent
}

/**
 * The profile photo (SH-94): pick → crop → upload, or remove. Both are optimistic: the avatar
 * shows the cropped picture (with the upload's progress) or the initials at once and goes back
 * to what was saved when the server refuses.
 */
class ProfilePhotoViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val uploadProfilePhoto: UploadProfilePhotoUseCase,
    private val removeProfilePhoto: RemoveProfilePhotoUseCase,
    private val processor: ProfilePhotoProcessor,
    private val avatarImages: AvatarImages? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(ProfilePhotoUiState())
    val uiState: StateFlow<ProfilePhotoUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            getCurrentUserUseCase().collect { user ->
                _uiState.update { state ->
                    // While a change is on its way the avatar keeps showing it, not the old profile.
                    if (state.uploadProgress != null || state.isRemoving) state.copy(uid = user?.uid, displayName = user?.displayName.orEmpty())
                    else state.copy(uid = user?.uid, displayName = user?.displayName.orEmpty(), photoUrl = user?.photoUrl)
                }
            }
        }
    }

    fun onEvent(event: ProfilePhotoEvent) {
        when (event) {
            is ProfilePhotoEvent.Picked -> prepare(event.bytes)
            is ProfilePhotoEvent.CropConfirmed -> upload(event.crop)
            ProfilePhotoEvent.CropCancelled -> _uiState.update { it.copy(cropSource = null) }
            ProfilePhotoEvent.Remove -> remove()
            ProfilePhotoEvent.MessageShown -> _uiState.update { it.copy(message = null, error = null) }
        }
    }

    private fun prepare(bytes: ByteArray?) {
        if (bytes == null) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.profile_photo_read_failed)) }
            return
        }
        if (_uiState.value.isBusy) return
        _uiState.update { it.copy(isPreparing = true, error = null) }
        viewModelScope.launch {
            try {
                val photo = processor.prepare(bytes)
                _uiState.update { it.copy(isPreparing = false, cropSource = photo) }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                _uiState.update { it.copy(isPreparing = false, error = e.photoText()) }
            }
        }
    }

    private fun upload(crop: IntRect) {
        val state = _uiState.value
        val source = state.cropSource ?: return
        val uid = state.uid ?: return
        if (state.uploadProgress != null || state.isRemoving) return
        val savedUrl = state.photoUrl
        _uiState.update { it.copy(cropSource = null, uploadProgress = 0f, error = null) }
        viewModelScope.launch {
            try {
                val rendered = processor.render(source, crop)
                _uiState.update { it.copy(preview = rendered.preview) }
                val url = uploadProfilePhoto(uid, rendered.jpeg) { progress ->
                    _uiState.update { if (it.uploadProgress != null) it.copy(uploadProgress = progress) else it }
                }.getOrThrow()
                // The avatar keeps the same picture: the new URL's image is the preview.
                avatarImages?.put(url, rendered.preview)
                _uiState.update {
                    it.copy(photoUrl = url, preview = null, uploadProgress = null, message = UiText.Resource(Res.string.profile_photo_updated))
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                _uiState.update { it.copy(photoUrl = savedUrl, preview = null, uploadProgress = null, error = e.photoText()) }
            }
        }
    }

    private fun remove() {
        val state = _uiState.value
        val uid = state.uid ?: return
        if (state.isBusy || !state.hasPhoto) return
        val savedUrl = state.photoUrl
        _uiState.update { it.copy(photoUrl = null, preview = null, isRemoving = true, error = null) }
        viewModelScope.launch {
            removeProfilePhoto(uid).fold(
                onSuccess = {
                    _uiState.update { it.copy(isRemoving = false, message = UiText.Resource(Res.string.profile_photo_removed)) }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(photoUrl = savedUrl, isRemoving = false, error = e.photoText()) }
                }
            )
        }
    }

    private fun Throwable.photoText(): UiText = when (this) {
        is PhotoProblemException -> UiText.Resource(
            when (problem) {
                PhotoProblemException.Problem.NOT_AN_IMAGE -> Res.string.profile_photo_not_an_image
                PhotoProblemException.Problem.TOO_LARGE -> Res.string.profile_photo_too_large
                PhotoProblemException.Problem.UNREADABLE -> Res.string.profile_photo_unreadable
            }
        )
        else -> toUiText()
    }
}
